import { useMemo } from 'react'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import i18n from 'modules/core/lib/i18n'
import { employeeUser } from 'modules/core/store/collections/users'

import type Employee from 'modules/employees/store/models/Employee'
import type LeaveForm from 'modules/timeoff/lib/LeaveForm'
import type Leave from 'modules/timeoff/store/models/Leave'

interface Copies {
  title: string
  description: string
}

const otherCopies = (leave: Leave, employee: Employee): Copies => {
  if (leave.isPending()) {
    return {
      title: i18n.t('time_off.admin.review.title'),
      description: i18n.tCurrent(
        'time_off.admin.review.subtitle',
        employeeUser(employee).isCurrent()
      ),
    }
  }

  if (leave.isRejected()) {
    return {
      title: i18n.t('time_off.admin.edit_rejected.title'),
      description: i18n.t('time_off.admin.rejected'),
    }
  }

  return {
    title: i18n.t('time_off.admin.edit_approved.title'),
    description: i18n.tCurrent(
      'time_off.admin.edit_approved.subtitle',
      employeeUser(employee).isCurrent()
    ),
  }
}

const buildPastBasicCopies = (form: LeaveForm): Copies => {
  if (form.isSickType()) {
    return {
      title: i18n.t('time_off.employee.review_approved_past_time_off.title'),
      description: i18n.t('time_off.employee.review_approved_past_time_off.subtitle.sick'),
    }
  }

  return {
    title: i18n.t('time_off.employee.review_approved_past_time_off.title'),
    description: i18n.t('time_off.employee.review_approved_past_time_off.subtitle.other'),
  }
}

const employeeCopies = (
  leave: Leave,
  form: LeaveForm,
  employee: Employee | null | undefined
): Copies => {
  if (leave.isRejected()) {
    return {
      title: i18n.t('time_off.admin.edit_rejected.title'),
      description: i18n.t('time_off.employee.rejected'),
    }
  }

  if (leave.isPending()) {
    return {
      title: i18n.t('time_off.employee.edit_pending.title'),
      description: i18n.t('time_off.employee.edit_pending.subtitle'),
    }
  }

  if (form.hasLeaveStarted()) return buildPastBasicCopies(form)

  if (form.isSickType()) {
    return {
      title: i18n.t('time_off.employee.edit_approved.title'),
      description: i18n.t('time_off.employee.edit_approved.subtitle.sick'),
    }
  }

  const manager = employee?.timeoffManager

  if (manager) {
    return {
      title: i18n.t('time_off.employee.edit_approved.title'),
      description: i18n.t('time_off.employee.edit_approved.subtitle.not_autoapproved', {
        timeoffManager: employeeUser(manager).fullName(),
      }),
    }
  }

  return {
    title: i18n.t('time_off.employee.edit_approved.title'),
    description: i18n.t('time_off.employee.edit_approved.subtitle.autoapproved'),
  }
}

interface Props {
  leave: Leave
  employee: Employee | null | undefined
  form: LeaveForm
  canApprove: boolean
}

const useModalCopies = ({ form, leave, employee }: Props): Copies => {
  const {
    access: { id: currentUserId },
  } = useCurrentSession()

  const { title, description } = useMemo<Copies>(() => {
    if (!employee) {
      return {
        title: '',
        description: '',
      }
    }

    if (employee.attributes.access_id === currentUserId) {
      return employeeCopies(leave, form, employee)
    }

    return otherCopies(leave, employee)
  }, [leave, form, employee, currentUserId])

  return {
    title,
    description,
  }
}

export default useModalCopies
